import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Pencil, Plus, Trash2 } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { AppIcon } from '../../components/AppIcon';
import { IconPicker } from '../../components/IconPicker';
import { TagPicker } from '../../components/TagPicker';
import { UserMultiSelect } from '../../components/UserMultiSelect';
import { Modal } from '../../components/Modal';
import { EmptyState, ErrorState, LoadingState, Spinner, useToast } from '../../components/Feedback';
import { ACCESS_MODE_LABELS, appColor, cn, STATUS_LABELS } from '../../lib/utils';
import type { AccessMatrixEntry, Application, Category, Environment, Network } from '../../types';

interface InstanceDraft {
    id?: string;
    label: string;
    url: string;
    environment: Environment;
    network: Network;
    requiresVpn: boolean;
    isPrimary: boolean;
    healthCheckUrl: string;
    k8sNamespace: string;
    notes: string;
}

interface ApplicationDraft {
    slug: string;
    name: string;
    shortDescription: string;
    description: string;
    categorySlug: string;
    icon: string;
    color: string;
    ownerTeam: string;
    documentationUrl: string;
    supportUrl: string;
    repositoryUrl: string;
    status: Application['status'];
    accessMode: Application['accessMode'];
    entraGroupId: string;
    allowedUserIds: string[];
    isFeatured: boolean;
    sortOrder: number;
    tags: string[];
    instances: InstanceDraft[];
}

const EMPTY_DRAFT: ApplicationDraft = {
    slug: '',
    name: '',
    shortDescription: '',
    description: '',
    categorySlug: '',
    icon: 'AppWindow',
    color: '#2563eb',
    ownerTeam: '',
    documentationUrl: '',
    supportUrl: '',
    repositoryUrl: '',
    status: 'active',
    accessMode: 'user_list',
    entraGroupId: '',
    allowedUserIds: [],
    isFeatured: false,
    sortOrder: 100,
    tags: [],
    instances: [],
};

function toDraft(application: Application): ApplicationDraft {
    return {
        slug: application.slug,
        name: application.name,
        shortDescription: application.shortDescription,
        description: application.description ?? '',
        categorySlug: application.category?.slug ?? '',
        icon: application.icon,
        color: application.color,
        ownerTeam: application.ownerTeam ?? '',
        documentationUrl: application.documentationUrl ?? '',
        supportUrl: application.supportUrl ?? '',
        repositoryUrl: application.repositoryUrl ?? '',
        status: application.status,
        accessMode: application.accessMode,
        entraGroupId: application.entraGroupId ?? '',
        // La liste nominative n'est pas portée par la projection du catalogue
        // (elle n'a de sens que pour une application en mode user_list, et
        // charger les identifiants de centaines de personnes sur chaque ligne
        // du tableau n'apporterait rien) : l'éditeur la charge à part, voir
        // l'effet de ApplicationEditor.
        allowedUserIds: [],
        isFeatured: application.isFeatured,
        sortOrder: application.sortOrder ?? 100,
        tags: [...application.tags],
        instances: application.instances.map((instance) => ({
            id: instance.id,
            label: instance.label,
            url: instance.url,
            environment: instance.environment,
            network: instance.network,
            requiresVpn: instance.requiresVpn,
            isPrimary: instance.isPrimary,
            // Servis par la projection d'administration : les relire évite que
            // l'enregistrement, qui remplace l'application entière, ne les efface.
            healthCheckUrl: instance.healthCheckUrl ?? '',
            k8sNamespace: instance.k8sNamespace ?? '',
            notes: instance.notes ?? '',
        })),
    };
}

/** Convertit le brouillon du formulaire en corps de requête accepté par l'API. */
function toPayload(draft: ApplicationDraft) {
    const optional = (value: string) => (value.trim() === '' ? null : value.trim());

    return {
        slug: draft.slug.trim(),
        name: draft.name.trim(),
        shortDescription: draft.shortDescription.trim(),
        description: optional(draft.description),
        categorySlug: optional(draft.categorySlug),
        icon: draft.icon.trim() || 'AppWindow',
        color: draft.color,
        ownerTeam: optional(draft.ownerTeam),
        documentationUrl: optional(draft.documentationUrl),
        supportUrl: optional(draft.supportUrl),
        repositoryUrl: optional(draft.repositoryUrl),
        status: draft.status,
        accessMode: draft.accessMode,
        entraGroupId: optional(draft.entraGroupId),
        allowedUserIds: draft.allowedUserIds,
        isFeatured: draft.isFeatured,
        sortOrder: draft.sortOrder,
        tags: draft.tags.map((tag) => tag.trim()).filter(Boolean),
        instances: draft.instances.map((instance) => ({
            ...(instance.id ? { id: instance.id } : {}),
            label: instance.label.trim(),
            url: instance.url.trim(),
            environment: instance.environment,
            network: instance.network,
            requiresVpn: instance.requiresVpn,
            isPrimary: instance.isPrimary,
            healthCheckUrl: optional(instance.healthCheckUrl),
            k8sNamespace: optional(instance.k8sNamespace),
            notes: optional(instance.notes),
        })),
    };
}

export function AdminApplicationsPage() {
    const [editing, setEditing] = useState<{ id: string | null; draft: ApplicationDraft } | null>(
        null
    );
    const [archiveTarget, setArchiveTarget] = useState<Application | null>(null);

    const queryClient = useQueryClient();
    const { notify } = useToast();

    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['admin-applications'],
        // Projection d'administration : elle seule porte entraGroupId, l'URL de
        // contrôle, le namespace et les notes, que l'enregistrement réécrit.
        queryFn: () => api.get<{ applications: Application[] }>('/api/admin/applications'),
    });

    const archive = useMutation({
        mutationFn: (id: string) => api.delete(`/api/admin/applications/${id}`),
        onSuccess: () => {
            notify('Application archivée.', 'success');
            void queryClient.invalidateQueries({ queryKey: ['catalog'] });
            void queryClient.invalidateQueries({ queryKey: ['admin-applications'] });
            setArchiveTarget(null);
        },
        onError: (err) =>
            notify(err instanceof ApiError ? err.message : 'L’archivage a échoué.', 'error'),
    });

    if (isLoading) return <LoadingState />;
    if (isError) {
        return (
            <ErrorState
                message={error instanceof Error ? error.message : 'Chargement impossible.'}
                onRetry={() => void refetch()}
            />
        );
    }

    const applications = data?.applications ?? [];

    return (
        <div className="space-y-4">
            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={() => setEditing({ id: null, draft: EMPTY_DRAFT })}
                    className="btn btn-primary"
                >
                    <Plus className="w-4 h-4" aria-hidden />
                    Ajouter une application
                </button>
            </div>

            {applications.length === 0 ? (
                <EmptyState
                    title="Catalogue vide"
                    description="Ajoutez une première application, ou chargez le référentiel initial avec le script de peuplement."
                />
            ) : (
                <div className="card overflow-x-auto">
                    <table className="table">
                        <thead>
                            <tr>
                                <th scope="col">Application</th>
                                <th scope="col">Catégorie</th>
                                <th scope="col">Mode d’accès</th>
                                <th scope="col">État</th>
                                <th scope="col">Instances</th>
                                <th scope="col" className="text-right">
                                    <span className="sr-only">Actions</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {applications.map((application) => (
                                <tr key={application.id}>
                                    <td>
                                        <div className="flex items-center gap-2.5">
                                            <span
                                                className="app-swatch flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
                                                style={appColor(application.color)}
                                                aria-hidden
                                            >
                                                <AppIcon
                                                    name={application.icon}
                                                    className="w-4 h-4"
                                                />
                                            </span>
                                            <div className="min-w-0">
                                                <span className="block font-medium text-[var(--color-text)]">
                                                    {application.name}
                                                </span>
                                                <span className="block text-xs text-[var(--color-text-muted)]">
                                                    {application.slug}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="text-[var(--color-text-secondary)]">
                                        {application.category?.name ?? '-'}
                                    </td>
                                    <td className="text-[var(--color-text-secondary)] text-xs">
                                        {ACCESS_MODE_LABELS[application.accessMode]}
                                    </td>
                                    <td className="text-[var(--color-text-secondary)] text-xs">
                                        {STATUS_LABELS[application.status]}
                                    </td>
                                    <td className="text-[var(--color-text-secondary)] tabular-nums">
                                        {application.instances.length}
                                    </td>
                                    <td className="text-right whitespace-nowrap">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setEditing({
                                                    id: application.id,
                                                    draft: toDraft(application),
                                                })
                                            }
                                            className="btn btn-ghost p-1.5"
                                            aria-label={`Modifier ${application.name}`}
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setArchiveTarget(application)}
                                            className="btn btn-ghost p-1.5 text-[var(--color-error)]"
                                            aria-label={`Archiver ${application.name}`}
                                        >
                                            <Archive className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && (
                <ApplicationEditor
                    applicationId={editing.id}
                    initialDraft={editing.draft}
                    onClose={() => setEditing(null)}
                />
            )}

            <Modal
                open={Boolean(archiveTarget)}
                title="Archiver cette application"
                description={archiveTarget?.name}
                onClose={() => setArchiveTarget(null)}
                footer={
                    <>
                        <button
                            type="button"
                            onClick={() => setArchiveTarget(null)}
                            className="btn btn-secondary"
                        >
                            Annuler
                        </button>
                        <button
                            type="button"
                            onClick={() => archive.mutate(archiveTarget!.id)}
                            disabled={archive.isPending}
                            className="btn btn-danger"
                        >
                            {archive.isPending && <Spinner className="w-4 h-4 text-current" />}
                            Archiver
                        </button>
                    </>
                }
            >
                <p className="text-sm text-[var(--color-text-secondary)]">
                    L’application disparaîtra du catalogue. Les droits et l’historique d’audit sont
                    conservés, rien n’est supprimé définitivement.
                </p>
            </Modal>
        </div>
    );
}

function ApplicationEditor({
    applicationId,
    initialDraft,
    onClose,
}: {
    applicationId: string | null;
    initialDraft: ApplicationDraft;
    onClose: () => void;
}) {
    const [draft, setDraft] = useState(initialDraft);
    const queryClient = useQueryClient();
    const { notify } = useToast();

    const { data: categories = [] } = useQuery({
        queryKey: ['categories'],
        queryFn: () => api.get<Category[]>('/api/catalog/categories'),
    });

    // La liste nominative n'est pas servie par /api/admin/applications (voir
    // toDraft) : elle est chargée à part, pour cette seule application, dès
    // que l'éditeur s'ouvre en mode user_list sur une application existante.
    const { data: accessEntries = [] } = useQuery({
        queryKey: ['admin-access', applicationId],
        queryFn: () =>
            api.get<AccessMatrixEntry[]>(`/api/admin/access?applicationId=${applicationId}`),
        enabled: Boolean(applicationId) && initialDraft.accessMode === 'user_list',
    });

    useEffect(() => setDraft(initialDraft), [initialDraft]);

    // Pré-remplit la sélection une fois la liste chargée, sans jamais écraser
    // une modification déjà faite par l'administrateur dans ce formulaire.
    useEffect(() => {
        if (accessEntries.length === 0) return;
        setDraft((current) =>
            current.allowedUserIds.length > 0
                ? current
                : { ...current, allowedUserIds: accessEntries.map((entry) => entry.userId) }
        );
    }, [accessEntries]);

    const patch = (values: Partial<ApplicationDraft>) =>
        setDraft((current) => ({ ...current, ...values }));

    const mutation = useMutation({
        mutationFn: () => {
            const payload = toPayload(draft);
            return applicationId
                ? api.put(`/api/admin/applications/${applicationId}`, payload)
                : api.post('/api/admin/applications', payload);
        },
        onSuccess: () => {
            notify(applicationId ? 'Application mise à jour.' : 'Application ajoutée.', 'success');
            void queryClient.invalidateQueries({ queryKey: ['catalog'] });
            void queryClient.invalidateQueries({ queryKey: ['admin-applications'] });
            void queryClient.invalidateQueries({ queryKey: ['categories'] });
            onClose();
        },
        onError: (err) =>
            notify(
                err instanceof ApiError ? err.message : 'L’enregistrement a échoué.',
                'error'
            ),
    });

    const isValid =
        draft.slug.trim().length >= 2 &&
        /^[a-z0-9-]+$/.test(draft.slug.trim()) &&
        draft.name.trim().length >= 2 &&
        draft.shortDescription.trim().length >= 5 &&
        draft.instances.every((i) => i.label.trim() && i.url.trim()) &&
        (draft.accessMode !== 'entra_group' || draft.entraGroupId.trim().length > 0);

    return (
        <Modal
            open
            size="lg"
            title={applicationId ? 'Modifier l’application' : 'Ajouter une application'}
            onClose={onClose}
            footer={
                <>
                    <button type="button" onClick={onClose} className="btn btn-secondary">
                        Annuler
                    </button>
                    <button
                        type="button"
                        onClick={() => mutation.mutate()}
                        disabled={!isValid || mutation.isPending}
                        className="btn btn-primary"
                    >
                        {mutation.isPending && <Spinner className="w-4 h-4 text-current" />}
                        Enregistrer
                    </button>
                </>
            }
        >
            <div className="space-y-5">
                <fieldset className="space-y-3">
                    <legend className="text-sm font-semibold text-[var(--color-text)]">
                        Identité
                    </legend>

                    <div className="grid gap-3 sm:grid-cols-2">
                        <Field label="Nom" required>
                            <input
                                value={draft.name}
                                onChange={(e) => patch({ name: e.target.value })}
                                className="input"
                                maxLength={160}
                            />
                        </Field>

                        <Field
                            label="Identifiant (slug)"
                            required
                            hint="Minuscules, chiffres et tirets uniquement. Sert d’adresse dans le Hub."
                        >
                            <input
                                value={draft.slug}
                                onChange={(e) => patch({ slug: e.target.value })}
                                className="input font-mono text-xs"
                                maxLength={96}
                            />
                        </Field>
                    </div>

                    <Field label="Description courte" required hint="Une ligne, 180 caractères au plus.">
                        <input
                            value={draft.shortDescription}
                            onChange={(e) => patch({ shortDescription: e.target.value })}
                            className="input"
                            maxLength={180}
                        />
                    </Field>

                    <Field label="Description complète">
                        <textarea
                            value={draft.description}
                            onChange={(e) => patch({ description: e.target.value })}
                            className="textarea resize-y"
                            rows={4}
                        />
                    </Field>

                    <div className="grid gap-3 sm:grid-cols-2">
                        <Field label="Catégorie">
                            <select
                                value={draft.categorySlug}
                                onChange={(e) => patch({ categorySlug: e.target.value })}
                                className="select"
                            >
                                <option value="">Aucune</option>
                                {categories.map((category) => (
                                    <option key={category.slug} value={category.slug}>
                                        {category.name}
                                    </option>
                                ))}
                            </select>
                        </Field>

                        <Field label="Couleur" hint="Elle teinte la pastille et la carte du catalogue.">
                            <input
                                type="color"
                                value={draft.color}
                                onChange={(e) => patch({ color: e.target.value })}
                                className="input h-9 p-1"
                            />
                        </Field>
                    </div>

                    {/* Le sélecteur prend la ligne entière : une grille d'icônes coincée
                        dans un tiers de largeur ne se parcourt pas. */}
                    <FieldGroup
                        label="Icône"
                        hint="Ouvrez la liste pour parcourir les icônes disponibles, ou filtrez-les par nom."
                    >
                        <IconPicker
                            value={draft.icon}
                            color={draft.color}
                            onChange={(icon) => patch({ icon })}
                        />
                    </FieldGroup>

                    <FieldGroup
                        label="Étiquettes"
                        hint="Le catalogue s’en sert pour filtrer. Réemployez celles qui existent avant d’en créer une."
                    >
                        <TagPicker value={draft.tags} onChange={(tags) => patch({ tags })} />
                    </FieldGroup>
                </fieldset>

                <fieldset className="space-y-3">
                    <legend className="text-sm font-semibold text-[var(--color-text)]">
                        Gouvernance
                    </legend>

                    <Field label="Équipe responsable">
                        <input
                            value={draft.ownerTeam}
                            onChange={(e) => patch({ ownerTeam: e.target.value })}
                            className="input"
                        />
                    </Field>

                    <div className="grid gap-3 sm:grid-cols-2">
                        <Field label="État">
                            <select
                                value={draft.status}
                                onChange={(e) =>
                                    patch({ status: e.target.value as Application['status'] })
                                }
                                className="select"
                            >
                                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                        </Field>

                        <label className="flex items-center gap-2 text-sm text-[var(--color-text)] self-end pb-2">
                            <input
                                type="checkbox"
                                checked={draft.isFeatured}
                                onChange={(e) => patch({ isFeatured: e.target.checked })}
                            />
                            Mettre en avant sur l’accueil
                        </label>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-3">
                        <Field label="Documentation">
                            <input
                                type="url"
                                value={draft.documentationUrl}
                                onChange={(e) => patch({ documentationUrl: e.target.value })}
                                className="input"
                                placeholder="https://"
                            />
                        </Field>
                        <Field label="Support">
                            <input
                                type="url"
                                value={draft.supportUrl}
                                onChange={(e) => patch({ supportUrl: e.target.value })}
                                className="input"
                                placeholder="https://"
                            />
                        </Field>
                        <Field label="Code source">
                            <input
                                type="url"
                                value={draft.repositoryUrl}
                                onChange={(e) => patch({ repositoryUrl: e.target.value })}
                                className="input"
                                placeholder="https://"
                            />
                        </Field>
                    </div>
                </fieldset>

                <fieldset className="space-y-3">
                    <legend className="flex items-center justify-between w-full text-sm font-semibold text-[var(--color-text)]">
                        Instances déployées
                    </legend>
                    <p className="text-xs text-[var(--color-text-muted)] -mt-2">
                        C’est ici que se déclarent l’environnement et le mode d’exposition. Une
                        application peut avoir une instance de production publique et une instance de
                        développement interne.
                    </p>

                    {draft.instances.map((instance, index) => (
                        <div
                            key={index}
                            className="p-3 border border-[var(--color-border)] rounded-lg space-y-2"
                        >
                            <div className="flex gap-2">
                                <input
                                    value={instance.label}
                                    onChange={(e) =>
                                        patch({
                                            instances: draft.instances.map((item, i) =>
                                                i === index ? { ...item, label: e.target.value } : item
                                            ),
                                        })
                                    }
                                    className="input"
                                    placeholder="Libellé, ex. Production"
                                />
                                <button
                                    type="button"
                                    onClick={() =>
                                        patch({
                                            instances: draft.instances.filter((_, i) => i !== index),
                                        })
                                    }
                                    className="btn btn-ghost p-2 text-[var(--color-error)]"
                                    aria-label="Retirer cette instance"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>

                            <input
                                type="url"
                                value={instance.url}
                                onChange={(e) =>
                                    patch({
                                        instances: draft.instances.map((item, i) =>
                                            i === index ? { ...item, url: e.target.value } : item
                                        ),
                                    })
                                }
                                className="input"
                                placeholder="https://exemple.clippit.fr"
                            />

                            <div className="grid gap-2 sm:grid-cols-2">
                                <select
                                    value={instance.environment}
                                    onChange={(e) =>
                                        patch({
                                            instances: draft.instances.map((item, i) =>
                                                i === index
                                                    ? {
                                                          ...item,
                                                          environment: e.target.value as Environment,
                                                      }
                                                    : item
                                            ),
                                        })
                                    }
                                    className="select"
                                >
                                    <option value="prod">Production</option>
                                    <option value="staging">Recette</option>
                                    <option value="dev">Développement</option>
                                    <option value="local">Local</option>
                                </select>

                                <select
                                    value={instance.network}
                                    onChange={(e) =>
                                        patch({
                                            instances: draft.instances.map((item, i) =>
                                                i === index
                                                    ? { ...item, network: e.target.value as Network }
                                                    : item
                                            ),
                                        })
                                    }
                                    className="select"
                                >
                                    <option value="public">Réseau public</option>
                                    <option value="private">Réseau interne</option>
                                </select>
                            </div>

                            <div className="flex flex-wrap gap-4">
                                <label className="flex items-center gap-2 text-xs text-[var(--color-text)]">
                                    <input
                                        type="checkbox"
                                        checked={instance.isPrimary}
                                        onChange={(e) =>
                                            patch({
                                                // Une seule instance principale par application.
                                                instances: draft.instances.map((item, i) => ({
                                                    ...item,
                                                    isPrimary:
                                                        i === index ? e.target.checked : false,
                                                })),
                                            })
                                        }
                                    />
                                    Instance principale
                                </label>

                                <label className="flex items-center gap-2 text-xs text-[var(--color-text)]">
                                    <input
                                        type="checkbox"
                                        checked={instance.requiresVpn}
                                        onChange={(e) =>
                                            patch({
                                                instances: draft.instances.map((item, i) =>
                                                    i === index
                                                        ? { ...item, requiresVpn: e.target.checked }
                                                        : item
                                                ),
                                            })
                                        }
                                    />
                                    Nécessite le VPN
                                </label>
                            </div>
                        </div>
                    ))}

                    <button
                        type="button"
                        onClick={() =>
                            patch({
                                instances: [
                                    ...draft.instances,
                                    {
                                        label: 'Production',
                                        url: '',
                                        environment: 'prod',
                                        network: 'public',
                                        requiresVpn: false,
                                        isPrimary: draft.instances.length === 0,
                                        healthCheckUrl: '',
                                        k8sNamespace: '',
                                        notes: '',
                                    },
                                ],
                            })
                        }
                        className="btn btn-secondary w-full"
                    >
                        <Plus className="w-4 h-4" aria-hidden />
                        Ajouter une instance
                    </button>
                </fieldset>

                <fieldset className="space-y-3">
                    <legend className="text-sm font-semibold text-[var(--color-text)]">
                        Qui a accès
                    </legend>

                    <Field
                        label="Mode d’accès"
                        hint="Tout le monde : accessible à tout collaborateur actif, sans réglage de plus. Liste d'utilisateurs : personnes choisies une à une ci-dessous. Groupe Entra ID : les membres du groupe obtiennent l'accès automatiquement, par synchronisation périodique."
                    >
                        <select
                            value={draft.accessMode}
                            onChange={(e) =>
                                patch({ accessMode: e.target.value as Application['accessMode'] })
                            }
                            className="select"
                        >
                            {Object.entries(ACCESS_MODE_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>
                                    {label}
                                </option>
                            ))}
                        </select>
                    </Field>

                    {draft.accessMode === 'entra_group' && (
                        <Field
                            label="Identifiant du groupe Entra ID"
                            required
                            hint="Les membres de ce groupe sont relus périodiquement et obtiennent l'accès sans action de votre part."
                        >
                            <input
                                value={draft.entraGroupId}
                                onChange={(e) => patch({ entraGroupId: e.target.value })}
                                className="input font-mono text-xs"
                                placeholder="GUID du groupe"
                            />
                        </Field>
                    )}

                    {draft.accessMode === 'user_list' && (
                        <FieldGroup
                            label="Utilisateurs autorisés"
                            hint="La liste ci-dessous remplace entièrement celle enregistrée : retirer une personne lui révoque l'accès."
                        >
                            <UserMultiSelect
                                value={draft.allowedUserIds}
                                onChange={(allowedUserIds) => patch({ allowedUserIds })}
                            />
                        </FieldGroup>
                    )}
                </fieldset>
            </div>
        </Modal>
    );
}

/**
 * Variante de `Field` pour un champ composé de plusieurs contrôles.
 *
 * `Field` enveloppe son contenu dans un « label », ce qui suppose un contrôle
 * unique à désigner. Le sélecteur d'icône et l'éditeur d'étiquettes en portent
 * plusieurs : leur intitulé est donc posé en simple titre de groupe.
 */
function FieldGroup({
    label,
    hint,
    children,
}: {
    label: string;
    hint?: string;
    children: React.ReactNode;
}) {
    return (
        <div>
            <p
                className={cn(
                    'text-sm font-medium text-[var(--color-text)]',
                    hint === undefined && 'mb-1.5'
                )}
            >
                {label}
            </p>
            {/* L'indication précède le contrôle : posée après, elle se retrouverait
                sous une grille d'icônes dépliée, à deux cents pixels de son champ. */}
            {hint && <p className="mt-0.5 mb-1.5 text-xs text-[var(--color-text-muted)]">{hint}</p>}
            {children}
        </div>
    );
}

function Field({
    label,
    required,
    hint,
    children,
}: {
    label: string;
    required?: boolean;
    hint?: string;
    children: React.ReactNode;
}) {
    return (
        <label className="block">
            <span className="block text-sm font-medium text-[var(--color-text)] mb-1.5">
                {label}
                {required && <span className="text-[var(--color-error)]"> *</span>}
            </span>
            {children}
            {hint && <span className="block mt-1 text-xs text-[var(--color-text-muted)]">{hint}</span>}
        </label>
    );
}
